import { useCallback } from 'react';
import type { MenuNode } from '@atlassian/jira-software-context-menu/src/common/types.tsx';
import { useEpicCardsState } from '@atlassian/jira-backlog-epics-store/src/selectors/index.tsx';
import type { EpicCardType } from '@atlassian/jira-backlog-epics-store/src/types.tsx';
import { usePanelData } from '../../../../../../context/use-panel-data.tsx';
import { useRankEpicsMutation } from '../../../../../../services/epic/rank/index.tsx';
import { useMoveToContent } from './view.tsx';

interface UseMoveToProps {
	cardId: number;
	setAnnouncement: (message: string) => void;
}

export const useMoveTo = ({ cardId, setAnnouncement }: UseMoveToProps): MenuNode[] | null => {
	const { boardId } = usePanelData();
	const [{ cards: epicCards }] = useEpicCardsState();
	const [rankEpics] = useRankEpicsMutation(boardId);

	const getOrderedIds = useCallback(() => {
		return epicCards.map((card: EpicCardType) => card.epic.id.toString());
	}, [epicCards]);

	const moveTo = useCallback(
		(direction: 'up' | 'down' | 'top' | 'bottom') => {
			const orderedIds = getOrderedIds();
			const currentIndex = orderedIds.indexOf(cardId.toString());

			if (currentIndex === -1) {
				return;
			}

			let targetIndex: number;
			switch (direction) {
				case 'up':
					targetIndex = Math.max(0, currentIndex - 1);
					break;
				case 'down':
					targetIndex = Math.min(orderedIds.length - 1, currentIndex + 1);
					break;
				case 'top':
					targetIndex = 0;
					break;
				case 'bottom':
					targetIndex = orderedIds.length - 1;
					break;
				default:
					return;
			}

			if (targetIndex === currentIndex) {
				return;
			}

			rankEpics([cardId], targetIndex);
		},
		[cardId, getOrderedIds, rankEpics],
	);

	const orderedIds = getOrderedIds();
	const currentIndex = orderedIds.indexOf(cardId.toString());
	const isFirst = currentIndex === 0;
	const isLast = currentIndex === orderedIds.length - 1;

	return useMoveToContent({
		isMoveToTopActionVisible: !isFirst,
		isMoveToBottomActionVisible: !isLast,
		isMoveUpActionVisible: !isFirst,
		isMoveDownActionVisible: !isLast,
		onMoveToTop: () => moveTo('top'),
		onMoveToBottom: () => moveTo('bottom'),
		onMoveUp: () => moveTo('up'),
		onMoveDown: () => moveTo('down'),
		setAnnouncement,
	});
};
